import { Box, Button, FormHelperText, Link, Stack, Typography } from "@mui/material";
import { ControlledCheckbox, ControlledTextField, Form, useHookForm } from "@powerdiary/components-library/lib";
import LoadingOverlay from "../../../Shared/LoadingOverlay";
import { defaultValues, loginSchema, LoginType } from "./LoginDialogForm.helpers";
import { useClientPortalUserLogin } from "./useClientPortalUserLogin";
import { LoginDialogMode } from "../../Account.types";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";

interface LoginDialogProps {
    loginSuccess?: () => void;
    setMode: (mode: LoginDialogMode) => void;
    returnUrl: string;
}

export default function LoginDialogForm({ loginSuccess, setMode, returnUrl }: Readonly<LoginDialogProps>) {
    const form = useHookForm({ defaultValues: defaultValues, schema: loginSchema });
    const { login, isPending } = useClientPortalUserLogin(form);
    const { clientPortalConfig } = useClientPortalConfigContext();

    const { formState } = form;
    const handleLogin = async (values: LoginType) => {
        await login(values, returnUrl, loginSuccess);
    };
    return (
        <Stack gap="24px">
            <Form form={form} onSubmit={handleLogin} dataTestIdSuffix="form_login">
                <Stack gap="16px">
                    <ControlledTextField dataTestIdSuffix="email" type="email" label="Email" name="email" />
                    <ControlledTextField dataTestIdSuffix="password" type="password" label="Password" name="password" />
                    {formState?.errors?.root && (
                        <FormHelperText error data-test-id="form_error_message">
                            <>{formState.errors.root.message}</>
                        </FormHelperText>
                    )}
                    <ControlledCheckbox dataTestIdSuffix="rememberMe" label="Remember me" name="rememberMe" />
                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={isPending}
                        data-test-id="button_login"
                    >
                        Log in
                    </Button>
                </Stack>
            </Form>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                <Link
                    data-test-id="link_reset_password"
                    variant="paragraphSmallRegular"
                    onClick={() => setMode(LoginDialogMode.Recover)}
                >
                    Forgot Password?
                </Link>
                {clientPortalConfig.newUsersCanRegister && (
                    <Typography variant="paragraphSmallRegular">
                        First time here?{" "}
                        <Link onClick={() => setMode(LoginDialogMode.Register)} data-test-id="link_register">
                            Register
                        </Link>
                    </Typography>
                )}
            </Box>
            {isPending && <LoadingOverlay />}
        </Stack>
    );
}
